import { Accordion, AccordionItem } from '@heroui/react';
import { useFormContext } from 'react-hook-form';
import { FormAutoComplete, FormCheckBox, FormInput } from '@/core/ui';
import type { AiConfigBaseName, IStationSchema } from './schema';

const STT_PROVIDER_OPTIONS = [
  { key: 'deepgram', label: 'Deepgram' },
  { key: 'openai', label: 'OpenAI' },
  { key: 'assemblyai', label: 'AssemblyAI' },
  { key: 'xai', label: 'X-AI' },
];

const DEEPGRAM_MODEL_OPTIONS = [
  { key: 'nova-3', label: 'Nova 3' },
  { key: 'nova-3-general', label: 'Nova 3 General' },
  { key: 'nova-3-medical', label: 'Nova 3 Medical' },
  { key: 'nova-2', label: 'Nova 2' },
  { key: 'nova-2-general', label: 'Nova 2 General' },
  { key: 'nova-2-medical', label: 'Nova 2 Medical' },
  { key: 'nova-2-phonecall', label: 'Nova 2 Phone Call' },
  { key: 'nova-2-meeting', label: 'Nova 2 Meeting' },
  { key: 'nova-2-conversationalai', label: 'Nova 2 Conversational AI' },
  { key: 'flux-general-en', label: 'Flux General (English)' },
  { key: 'flux-general-multi', label: 'Flux General (Multilingual)' },
  { key: 'enhanced-general', label: 'Enhanced General' },
  { key: 'base-general', label: 'Base General' },
];

const OPENAI_STT_MODEL_OPTIONS = [
  { key: 'gpt-4o-transcribe', label: 'GPT-4o Transcribe' },
  { key: 'gpt-4o-mini-transcribe', label: 'GPT-4o Mini Transcribe' },
  { key: 'whisper-1', label: 'Whisper-1 (Legacy)' },
];

const ASSEMBLYAI_MODEL_OPTIONS = [
  { key: 'u3-rt-pro', label: 'Universal-3 Pro Streaming' },
  { key: 'universal-streaming-multilingual', label: 'Universal Streaming Multilingual' },
  { key: 'universal-streaming', label: 'Universal Streaming' },
];

const XAI_STT_MODEL_OPTIONS = [{ key: 'stt-1', label: 'Speech to Text' }];

export const AiConfigSttTab = ({ baseName }: { baseName: AiConfigBaseName }) => {
  const form = useFormContext<IStationSchema>();

  return (
    <div className='grid grid-cols-2 gap-4 pt-3'>
      <FormAutoComplete<IStationSchema>
        control={form.control}
        name={`${baseName}.stt.provider`}
        label='Active provider'
        placeholder='Select STT provider'
        options={STT_PROVIDER_OPTIONS}
        variant='faded'
        className='rounded col-span-2'
      />

      <div className='col-span-2'>
        <Accordion variant='bordered' selectionMode='multiple' keepContentMounted>
          <AccordionItem key='deepgram' aria-label='Deepgram' title='Deepgram'>
            <div className='grid grid-cols-2 gap-4 pt-1'>
              <FormAutoComplete<IStationSchema>
                control={form.control}
                name={`${baseName}.stt.deepgram.model`}
                label='Model'
                placeholder='Select model'
                options={DEEPGRAM_MODEL_OPTIONS}
                variant='faded'
                className='rounded'
              />
              <FormInput<IStationSchema>
                control={form.control}
                name={`${baseName}.stt.deepgram.language`}
                label='Language'
                placeholder='en-GB'
                variant='faded'
                className='rounded'
              />
              <FormCheckBox<IStationSchema>
                control={form.control}
                shouldUnregister={false}
                name={`${baseName}.stt.deepgram.enable_diarization`}
              >
                Enable diarization
              </FormCheckBox>
              {/* <FormInput<IStationSchema>
                control={form.control}
                name={`${baseName}.stt.deepgram.endpointing`}
                label='Endpointing (ms)'
                placeholder='300'
                variant='faded'
                className='rounded'
              />
              <FormInput<IStationSchema>
                control={form.control}
                name={`${baseName}.stt.deepgram.keyterms`}
                label='Keyterms (comma separated)'
                placeholder='myocardial infarction, troponin'
                variant='faded'
                className='rounded'
              />
              <FormCheckBox<IStationSchema>
                control={form.control}
                name={`${baseName}.stt.deepgram.smart_format`}
              >
                Smart format
              </FormCheckBox>
              <FormCheckBox<IStationSchema>
                control={form.control}
                name={`${baseName}.stt.deepgram.interim_results`}
              >
                Interim results
              </FormCheckBox> */}
            </div>
          </AccordionItem>

          <AccordionItem key='openai' aria-label='OpenAI Whisper' title='OpenAI Whisper'>
            <div className='grid grid-cols-2 gap-4 pt-1'>
              <FormAutoComplete<IStationSchema>
                control={form.control}
                name={`${baseName}.stt.openai.model`}
                label='Model'
                placeholder='Select model'
                options={OPENAI_STT_MODEL_OPTIONS}
                variant='faded'
                className='rounded'
              />
              <FormInput<IStationSchema>
                control={form.control}
                name={`${baseName}.stt.openai.language`}
                label='Language'
                placeholder='en'
                variant='faded'
                className='rounded'
              />
              {/* <FormInput<IStationSchema>
                control={form.control}
                name={`${baseName}.stt.openai.temperature`}
                label='Temperature'
                placeholder='0.0'
                variant='faded'
                className='rounded'
              />
              <FormTextArea<IStationSchema>
                control={form.control}
                name={`${baseName}.stt.openai.prompt`}
                label='Prompt'
                placeholder='Medical OSCE consultation...'
                variant='faded'
                className='rounded col-span-2'
              /> */}
            </div>
          </AccordionItem>

          <AccordionItem key='assemblyai' aria-label='AssemblyAI' title='AssemblyAI'>
            <div className='grid grid-cols-2 gap-4 pt-1'>
              <FormAutoComplete<IStationSchema>
                control={form.control}
                name={`${baseName}.stt.assemblyai.model`}
                label='Model'
                placeholder='Select model'
                options={ASSEMBLYAI_MODEL_OPTIONS}
                variant='faded'
                className='rounded'
              />
              <FormInput<IStationSchema>
                control={form.control}
                name={`${baseName}.stt.assemblyai.language`}
                label='Language'
                placeholder='en'
                variant='faded'
                className='rounded'
              />
              <FormCheckBox<IStationSchema>
                control={form.control}
                shouldUnregister={false}
                name={`${baseName}.stt.assemblyai.speaker_labels`}
              >
                Speaker labels
              </FormCheckBox>
              {/* <FormInput<IStationSchema>
                control={form.control}
                name={`${baseName}.stt.assemblyai.keyterms_prompt`}
                label='Keyterms prompt (comma separated)'
                placeholder='troponin, ECG, STEMI'
                variant='faded'
                className='rounded col-span-2'
              />
              <FormInput<IStationSchema>
                control={form.control}
                name={`${baseName}.stt.assemblyai.min_turn_silence`}
                label='Min turn silence (ms)'
                placeholder='300'
                variant='faded'
                className='rounded'
              />
              <FormInput<IStationSchema>
                control={form.control}
                name={`${baseName}.stt.assemblyai.max_turn_silence`}
                label='Max turn silence (ms)'
                placeholder='1200'
                variant='faded'
                className='rounded'
              />
              <FormInput<IStationSchema>
                control={form.control}
                name={`${baseName}.stt.assemblyai.vad_threshold`}
                label='VAD threshold'
                placeholder='0.3'
                variant='faded'
                className='rounded'
              /> */}
            </div>
          </AccordionItem>

          <AccordionItem key='xai' aria-label='XAI' title='XAI'>
            <div className='grid grid-cols-2 gap-4 pt-1'>
              <FormAutoComplete<IStationSchema>
                control={form.control}
                name={`${baseName}.stt.xai.model`}
                label='Model'
                placeholder='Select model'
                options={XAI_STT_MODEL_OPTIONS}
                variant='faded'
                className='rounded'
              />
              <FormInput<IStationSchema>
                control={form.control}
                name={`${baseName}.stt.xai.language`}
                label='Language'
                placeholder='en'
                variant='faded'
                className='rounded'
              />
              <FormCheckBox<IStationSchema>
                control={form.control}
                shouldUnregister={false}
                name={`${baseName}.stt.xai.enable_diarization`}
              >
                Enable diarization
              </FormCheckBox>
            </div>
          </AccordionItem>
        </Accordion>
      </div>
    </div>
  );
};
